import React, { Fragment, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, Table, theme as antdTheme } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import KpiCards, {
  KpiCardDef,
} from '@totem/components/services/optimize/v3/kpiCards';
import {
  getPipelineBucket,
  isQuickWin,
  PIPELINE_STAGES,
  PipelineBucketKey,
  totalOrAnnual,
} from '@totem/components/services/optimize/v3/pipeline';
import TicketingContext from '@totem/components/ticketing/listContainer/ticketingContainerContext';
import {
  getOpportunityStatus,
  OpportunityStatus,
} from '@totem/components/ticketing/opportunityStatus';
import {
  calculateAnnualSavings,
  formatSavingsTimeframe,
  isVisibleOpportunity,
} from '@totem/components/ticketing/ticket/specialCases/utilities';
import { TicketQueryResult } from '@totem/components/ticketing/ticketing';
import TagDisplay, {
  LEVEL_OF_EFFORT_STYLES,
  SCOPE_OF_RETURN_STYLES,
} from '@totem/components/ui_components/widgets/tagDisplay/tagDisplay';

const formatCompact = (val: number): string => {
  if (val >= 1_000_000) {
    const mVal = val / 1_000_000;
    return `$${mVal % 1 === 0 ? Math.round(mVal) : mVal.toFixed(1)}M`;
  }
  if (val >= 1000) {
    const kVal = val / 1000;
    return `$${kVal % 1 === 0 ? Math.round(kVal) : kVal.toFixed(1)}K`;
  }
  return `$${Math.round(val).toLocaleString('en-US')}`;
};

const formatFull = (val: number): string =>
  `$${Math.round(val).toLocaleString('en-US')}`;

const ACTIVE_STATUSES = new Set<OpportunityStatus>([
  'Draft',
  'Pending Review',
  'Approved',
  'In Progress',
  'Verifying',
  'Deferred',
]);

const UNSET_PATTERN = /^(not set|uncategorized|unknown|other|)$/i;
const isUnsetKey = (key: string): boolean => UNSET_PATTERN.test(key);

// ---------------------------------------------------------------------------
// Composition breakdown (by savings type / by category)
// ---------------------------------------------------------------------------

type CompositionCardProps = {
  title: string;
  subtitle: string;
  data: Record<string, number>;
};

const CompositionCard = ({ title, subtitle, data }: CompositionCardProps) => {
  const { useToken } = antdTheme;
  const { token } = useToken();

  const keys = Object.keys(data).sort((keyA, keyB) => {
    const unsetA = isUnsetKey(keyA) ? 1 : 0;
    const unsetB = isUnsetKey(keyB) ? 1 : 0;
    if (unsetA !== unsetB) return unsetA - unsetB;
    return data[keyB] - data[keyA];
  });

  if (keys.length === 0) {
    return (
      <Card size="small" title={title}>
        <div style={{ fontSize: 12.5, color: token.colorTextTertiary }}>
          No scored opportunities in scope.
        </div>
      </Card>
    );
  }

  const total = keys.reduce((sum, key) => sum + data[key], 0);
  const max = Math.max(...keys.map((key) => data[key]), 1);
  const unsetTotal = keys
    .filter(isUnsetKey)
    .reduce((sum, key) => sum + data[key], 0);

  return (
    <Card size="small" title={title}>
      <div
        style={{
          fontSize: 12,
          color: token.colorTextTertiary,
          marginBottom: 12,
        }}
      >
        {subtitle}
      </div>
      {keys.map((key) => {
        const pct = Math.round((data[key] / total) * 100);
        return (
          <div key={key} style={{ marginBottom: 12 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 13,
              }}
            >
              <span>
                {key || 'Not set'}{' '}
                <span style={{ color: token.colorTextTertiary, fontSize: 12 }}>
                  {pct}%
                </span>
              </span>
              <span style={{ fontWeight: 600 }}>
                {formatCompact(data[key])}
              </span>
            </div>
            <div
              style={{
                height: 6,
                background: token.colorBorderSecondary,
                borderRadius: 3,
                marginTop: 4,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${Math.max(Math.round((data[key] / max) * 100), 2)}%`,
                  background: '#E8540A',
                  borderRadius: 3,
                }}
              />
            </div>
          </div>
        );
      })}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          marginTop: 12,
          paddingTop: 10,
          fontWeight: 600,
          fontSize: 13,
        }}
      >
        <span>Total</span>
        <span>{formatCompact(total)}</span>
      </div>
      {unsetTotal > 0 && (
        <div
          style={{
            marginTop: 8,
            paddingTop: 8,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            fontSize: 12,
            color: '#B45309',
          }}
        >
          {formatCompact(unsetTotal)} of this (
          {Math.round((unsetTotal / total) * 100)}%) is not yet classified.
        </div>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Effort × return matrix
// ---------------------------------------------------------------------------

const EFFORT_ROWS = ['High', 'Medium', 'Low'] as const;
const RETURN_COLS = ['Low', 'Medium', 'High'] as const;

type MatrixCell = { count: number; value: number };

const tierBackground = (ratio: number): string => {
  if (ratio > 0.6) return 'rgba(232, 84, 10, 0.55)';
  if (ratio > 0.25) return 'rgba(232, 84, 10, 0.3)';
  if (ratio > 0) return 'rgba(232, 84, 10, 0.14)';
  return 'transparent';
};

const EffortReturnMatrix = ({
  openTickets,
}: {
  openTickets: TicketQueryResult[];
}) => {
  const { useToken } = antdTheme;
  const { token } = useToken();

  const grid = useMemo(() => {
    const map: Record<string, MatrixCell> = {};
    openTickets.forEach((rec) => {
      const opt = rec.ticket.extras?.optimize;
      if (!opt?.levelOfEffort || !opt?.scopeOfReturn) return;
      const key = `${opt.levelOfEffort}|${opt.scopeOfReturn}`;
      if (!map[key]) map[key] = { count: 0, value: 0 };
      map[key].count += 1;

      map[key].value += totalOrAnnual(rec);
    });
    return map;
  }, [openTickets]);

  const maxValue = Math.max(...Object.values(grid).map((cel) => cel.value), 1);

  const unscoredCount = openTickets.filter((rec) => {
    const opt = rec.ticket.extras?.optimize;
    return !opt?.levelOfEffort || !opt?.scopeOfReturn;
  }).length;

  return (
    <Card size="small" title="Where the money is">
      <div
        style={{
          fontSize: 12,
          color: token.colorTextTertiary,
          marginBottom: 12,
        }}
      >
        Savings over the term, by level of effort (rows) × scope of return
        (columns). The outlined cell is the quick-win corner.
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '34px repeat(3, 1fr)',
          gap: 6,
        }}
      >
        <div />
        {RETURN_COLS.map((ret) => (
          <div
            key={ret}
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              color: token.colorTextTertiary,
              textAlign: 'center',
            }}
          >
            {ret}
          </div>
        ))}
        {EFFORT_ROWS.map((eff) => (
          <Fragment key={eff}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                color: token.colorTextTertiary,
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {eff}
            </div>
            {RETURN_COLS.map((ret) => {
              const cell = grid[`${eff}|${ret}`];
              const isWin = eff === 'Low' && ret === 'High';

              return (
                <div
                  key={ret}
                  style={{
                    border: isWin
                      ? '2px solid #E8540A'
                      : `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: 8,
                    padding: 10,
                    minHeight: 76,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    background: cell
                      ? tierBackground(cell.value / maxValue)
                      : token.colorFillTertiary,
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      color: cell ? token.colorText : token.colorTextQuaternary,
                    }}
                  >
                    {cell
                      ? `${cell.count} ${cell.count === 1 ? 'opp' : 'opps'}`
                      : '—'}
                  </div>
                  <div
                    style={{
                      fontSize: 17,
                      fontWeight: 700,
                      color: cell ? token.colorText : token.colorTextQuaternary,
                    }}
                  >
                    {cell ? formatCompact(cell.value) : '—'}
                  </div>
                </div>
              );
            })}
          </Fragment>
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 11,
          color: token.colorTextTertiary,
          marginTop: 8,
          paddingLeft: 40,
        }}
      >
        <span>← Scope of return →</span>
        <span>Darker = more value</span>
      </div>
      {unscoredCount > 0 && (
        <div
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            fontSize: 12.5,
            color: token.colorTextSecondary,
          }}
        >
          <strong style={{ color: '#B45309' }}>
            {unscoredCount} open{' '}
            {unscoredCount === 1 ? 'opportunity is' : 'opportunities are'} not
            yet scored
          </strong>{' '}
          for effort or return, so they sit outside this grid.
        </div>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Approval pipeline card
// ---------------------------------------------------------------------------

const PipelineCard = ({
  allTickets,
  onSwitchTab,
}: {
  allTickets: TicketQueryResult[];
  onSwitchTab?: (key: string) => void;
}) => {
  const { useToken } = antdTheme;
  const { token } = useToken();

  const buckets = useMemo(() => {
    const acc: Record<PipelineBucketKey, { count: number; annual: number }> = {
      pendingReview: { count: 0, annual: 0 },
      approved: { count: 0, annual: 0 },
      inProgress: { count: 0, annual: 0 },
      verifying: { count: 0, annual: 0 },
      confirm: { count: 0, annual: 0 },
      booked: { count: 0, annual: 0 },
    };

    allTickets.forEach((rec) => {
      const bucket = getPipelineBucket(rec);
      if (!bucket) return;
      const opt = rec.ticket.extras?.optimize;
      acc[bucket].count += 1;

      acc[bucket].annual += opt ? calculateAnnualSavings(opt) : 0;
    });

    return acc;
  }, [allTickets]);

  const maxCount = Math.max(
    ...PIPELINE_STAGES.map((stage) => buckets[stage.key].count),
    1,
  );

  return (
    <Card size="small" title="Approval pipeline">
      <div
        style={{
          fontSize: 12,
          color: token.colorTextTertiary,
          marginBottom: 16,
        }}
      >
        Every opportunity sits in exactly one of six states.
      </div>
      {PIPELINE_STAGES.map((stage) => {
        const bucket = buckets[stage.key];
        const widthPct =
          bucket.count > 0
            ? Math.max(Math.round((bucket.count / maxCount) * 100), 3)
            : 0;
        const fillColor =
          stage.key === 'booked'
            ? '#27AE60'
            : bucket.count === 0
              ? token.colorBorderSecondary
              : '#122048';

        return (
          <div key={stage.key} style={{ marginBottom: 16 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 6,
              }}
            >
              <span style={{ fontSize: 13 }}>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: token.colorTextTertiary,
                    letterSpacing: '0.06em',
                    marginRight: 6,
                  }}
                >
                  {stage.number}
                </span>
                {stage.label}
              </span>
              <span
                style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                {bucket.count}
                {bucket.annual > 0
                  ? `  ${formatCompact(bucket.annual)}/yr`
                  : ''}
              </span>
            </div>
            <div
              style={{
                height: 6,
                background: token.colorBorderSecondary,
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${widthPct}%`,
                  background: fillColor,
                  borderRadius: 3,
                }}
              />
            </div>
          </div>
        );
      })}
      {onSwitchTab && (
        <a onClick={() => onSwitchTab('lifecycle')} style={{ fontSize: 13 }}>
          What happens in each state →
        </a>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type Props = {
  onSwitchTab?: (key: string) => void;
};

const OptimizeOverviewTab = ({ onSwitchTab }: Props) => {
  const { ticketData } = useContext(TicketingContext);
  const { useToken } = antdTheme;
  const { token } = useToken();

  const allTickets = useMemo(
    () => (ticketData?.tickets ?? []).filter(isVisibleOpportunity),
    [ticketData],
  );

  const openTickets = useMemo(
    () =>
      allTickets.filter((rec) =>
        ACTIVE_STATUSES.has(getOpportunityStatus(rec)),
      ),
    [allTickets],
  );

  const quickWins = useMemo(
    () => openTickets.filter(isQuickWin),
    [openTickets],
  );

  const bookedTickets = useMemo(
    () =>
      allTickets.filter(
        (rec) => getOpportunityStatus(rec) === 'Savings Booked',
      ),
    [allTickets],
  );

  const kpis = useMemo(() => {
    const scoredOpen = openTickets.filter(
      (rec) => (rec.ticket.extras?.optimize?.savingsPotential ?? 0) > 0,
    );

    const annualSavingsAtStake = scoredOpen.reduce(
      (sum, rec) => sum + calculateAnnualSavings(rec.ticket.extras!.optimize!),
      0,
    );
    const totalOverTerms = scoredOpen.reduce(
      (sum, rec) => sum + totalOrAnnual(rec),
      0,
    );
    const capitalRequired = openTickets.reduce(
      (sum, rec) =>
        sum + (rec.ticket.extras?.optimize?.requiredInvestment ?? 0),
      0,
    );
    const quickWinAnnual = quickWins.reduce((sum, rec) => {
      const opt = rec.ticket.extras?.optimize;
      return sum + (opt ? calculateAnnualSavings(opt) : 0);
    }, 0);
    const unscoredCount = openTickets.length - scoredOpen.length;

    return {
      annualSavingsAtStake,
      totalOverTerms,
      capitalRequired,
      quickWinAnnual,
      unscoredCount,
    };
  }, [openTickets, quickWins]);

  const composition = useMemo(() => {
    const byType: Record<string, number> = {};
    const byCategory: Record<string, number> = {};

    openTickets.forEach((rec) => {
      const opt = rec.ticket.extras?.optimize;
      if (!opt || !(opt.savingsPotential > 0)) return;
      const annual = calculateAnnualSavings(opt);
      const typeKey = opt.savingsType || 'Not set';
      const catKey = opt.category || 'Not set';
      byType[typeKey] = (byType[typeKey] ?? 0) + annual;

      byCategory[catKey] = (byCategory[catKey] ?? 0) + annual;
    });

    return { byType, byCategory };
  }, [openTickets]);

  const topOpportunities = useMemo(
    () =>
      [...openTickets]
        .sort((recA, recB) => totalOrAnnual(recB) - totalOrAnnual(recA))
        .slice(0, 6),
    [openTickets],
  );

  const kpiCards: KpiCardDef[] = [
    {
      key: 'quickWins',
      label: 'Quick Wins Ready',
      value: quickWins.length,
      sub:
        quickWins.length > 0
          ? `${formatCompact(kpis.quickWinAnnual)}/yr · low effort, high return`
          : 'none scoped as low effort / high return',
      tone: quickWins.length > 0 ? 'accent' : 'default',
      onClick: onSwitchTab ? () => onSwitchTab('opportunities') : undefined,
    },
    {
      key: 'open',
      label: 'Open Opportunities',
      value: openTickets.length,
      sub: `of ${allTickets.length} total · ${bookedTickets.length} realized`,
      onClick: onSwitchTab ? () => onSwitchTab('opportunities') : undefined,
    },
    {
      key: 'savings',
      label: 'Annual Savings at Stake',
      value: formatCompact(kpis.annualSavingsAtStake),
      sub: `${formatCompact(kpis.totalOverTerms)} over the full terms`,
      tone: 'good',
    },
    {
      key: 'capital',
      label: 'Capital Required',
      value: formatFull(kpis.capitalRequired),
      sub: `across all ${openTickets.length} open`,
    },
  ];

  const topColumns: ColumnProps<TicketQueryResult>[] = [
    {
      title: '#',
      key: 'number',
      width: 110,
      render: (_, rec) => (
        <Link to={`/dashboard/optimize/opportunity/${rec.ticket.id}`}>
          {rec.ticket.number}
        </Link>
      ),
    },
    {
      title: 'Opportunity',
      key: 'summary',
      render: (_, rec) => (
        <Link to={`/dashboard/optimize/opportunity/${rec.ticket.id}`}>
          {rec.ticket.summary}
        </Link>
      ),
    },
    {
      title: 'Investment',
      key: 'investment',
      width: 110,
      render: (_, rec) =>
        formatFull(rec.ticket.extras?.optimize?.requiredInvestment ?? 0),
    },
    {
      title: 'Annual',
      key: 'annual',
      width: 110,
      render: (_, rec) => {
        const opt = rec.ticket.extras?.optimize;
        return opt ? formatFull(calculateAnnualSavings(opt)) : '—';
      },
    },
    {
      title: 'Over Term',
      key: 'overTerm',
      width: 120,
      render: (_, rec) => (
        <span style={{ color: '#16A34A', fontWeight: 600 }}>
          {formatCompact(totalOrAnnual(rec))}
        </span>
      ),
    },
    {
      title: 'Term',
      key: 'term',
      width: 90,
      render: (_, rec) => {
        const opt = rec.ticket.extras?.optimize;
        return opt && opt.savingsTerm > 0 ? formatSavingsTimeframe(opt) : '—';
      },
    },
    {
      title: 'Effort',
      key: 'effort',
      width: 90,
      render: (_, rec) =>
        rec.ticket.extras?.optimize?.levelOfEffort ? (
          <TagDisplay
            value={rec.ticket.extras.optimize.levelOfEffort}
            config={LEVEL_OF_EFFORT_STYLES}
          />
        ) : (
          '—'
        ),
    },
    {
      title: 'Return',
      key: 'return',
      width: 90,
      render: (_, rec) =>
        rec.ticket.extras?.optimize?.scopeOfReturn ? (
          <TagDisplay
            value={rec.ticket.extras.optimize.scopeOfReturn}
            config={SCOPE_OF_RETURN_STYLES}
          />
        ) : (
          '—'
        ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          marginBottom: 20,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 24,
              fontWeight: 700,
              color: token.colorText,
              marginBottom: 6,
              lineHeight: 1.2,
            }}
          >
            {openTickets.length === 0
              ? 'No open opportunities.'
              : `You have ${openTickets.length} open ${openTickets.length === 1 ? 'opportunity' : 'opportunities'}.`}
          </div>
          <div
            style={{
              fontSize: 14,
              color: token.colorTextSecondary,
              lineHeight: 1.6,
              maxWidth: '62ch',
            }}
          >
            {openTickets.length === 0 ? (
              `${bookedTickets.length} opportunity${bookedTickets.length === 1 ? '' : 'ies'} already realized.`
            ) : (
              <>
                IB has identified{' '}
                <strong>
                  {formatCompact(kpis.annualSavingsAtStake)} of annual savings
                </strong>{' '}
                in this portfolio.{' '}
                {quickWins.length > 0 ? (
                  <>
                    <strong>
                      {quickWins.length}{' '}
                      {quickWins.length === 1
                        ? 'is a quick win'
                        : 'are quick wins'}
                    </strong>{' '}
                    — low effort, high return, and little to no capital.
                  </>
                ) : (
                  'None are scored as low effort with a high return, so each one needs a real conversation rather than a quick sign-off.'
                )}
                {kpis.unscoredCount > 0 &&
                  ` ${kpis.unscoredCount} further open ${kpis.unscoredCount === 1 ? 'opportunity has' : 'opportunities have'} no savings value recorded and so ${kpis.unscoredCount === 1 ? 'is' : 'are'} not counted above.`}
              </>
            )}
          </div>
        </div>
        {onSwitchTab && (
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <a
              onClick={() => onSwitchTab('opportunities')}
              style={{
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: 7,
                padding: '6px 12px',
                fontSize: 13,
                fontWeight: 500,
                color: token.colorText,
              }}
            >
              All opportunities{' '}
              <span style={{ color: token.colorTextTertiary }}>
                {allTickets.length}
              </span>
            </a>
          </div>
        )}
      </div>

      <div style={{ marginBottom: 20 }}>
        <KpiCards cards={kpiCards} />
      </div>

      {(Object.keys(composition.byType).length > 0 ||
        Object.keys(composition.byCategory).length > 0) && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            marginBottom: 20,
          }}
        >
          <CompositionCard
            title="How the savings arise"
            subtitle="Annual value of open opportunities by savings type"
            data={composition.byType}
          />
          <CompositionCard
            title="Where the savings arise"
            subtitle="Annual value of open opportunities by system category"
            data={composition.byCategory}
          />
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          marginBottom: 20,
        }}
      >
        <EffortReturnMatrix openTickets={openTickets} />
        <PipelineCard allTickets={allTickets} onSwitchTab={onSwitchTab} />
      </div>

      <Card
        size="small"
        title="Largest open opportunities"
        extra={
          onSwitchTab ? (
            <a onClick={() => onSwitchTab('opportunities')}>View all →</a>
          ) : (
            <Link to="/dashboard/optimize3?t=opportunities">View all →</Link>
          )
        }
      >
        <div
          style={{
            fontSize: 12,
            color: token.colorTextTertiary,
            marginBottom: 8,
          }}
        >
          Showing {topOpportunities.length} of {openTickets.length} open ·
          ranked by savings over the service term
        </div>
        <Table
          rowKey={(rec) => rec.ticket.id}
          columns={topColumns}
          dataSource={topOpportunities}
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
};

export default OptimizeOverviewTab;
